/**
 * Which Stage — second mainstage. Lower roof, simpler structure.
 */

import * as THREE from 'three';
import { type StageHandle, makeScreenQuad } from './StageBase';

export class WhichStage {
  static build(): StageHandle {
    const group = new THREE.Group();
    group.name = 'WhichStage';

    const matSteel = new THREE.MeshStandardMaterial({
      color: '#3a3038',
      roughness: 0.55,
      metalness: 0.7
    });
    const matDeck = new THREE.MeshStandardMaterial({
      color: '#1c1820',
      roughness: 0.85
    });
    const matScreen = new THREE.MeshBasicMaterial({ color: '#0a0a14' });
    const matAccent = new THREE.MeshStandardMaterial({
      color: '#7ce0a3',
      emissive: '#7ce0a3',
      emissiveIntensity: 0.4
    });

    const deckW = 40;
    const deckD = 18;
    const deckH = 2.2;

    const deck = new THREE.Mesh(new THREE.BoxGeometry(deckW, deckH, deckD), matDeck);
    deck.position.y = deckH / 2;
    deck.castShadow = true;
    deck.receiveShadow = true;
    group.add(deck);

    // Side towers
    const colH = 16;
    for (const sign of [-1, 1] as const) {
      const tower = new THREE.Mesh(
        new THREE.BoxGeometry(1.4, colH, 1.4),
        matSteel
      );
      tower.position.set(sign * (deckW / 2 - 1), colH / 2 + deckH, -deckD / 2 + 0.7);
      tower.castShadow = true;
      group.add(tower);

      const tower2 = tower.clone();
      tower2.position.z = deckD / 2 - 0.7;
      group.add(tower2);
    }

    // Angled roof (two flat planes sloping back)
    const roofW = deckW + 2;
    const roofD = deckD * 0.7;
    const roofGeo = new THREE.BoxGeometry(roofW, 0.6, roofD);
    const roof = new THREE.Mesh(roofGeo, matSteel);
    roof.position.set(0, colH + deckH + 0.3, -deckD / 4);
    roof.rotation.x = -0.18;
    roof.castShadow = true;
    group.add(roof);

    // Backdrop screen
    const screenW = 18;
    const screenH = 10;
    const screenFrame = new THREE.Mesh(
      new THREE.BoxGeometry(screenW + 0.5, screenH + 0.5, 0.4),
      matSteel
    );
    screenFrame.position.set(0, deckH + screenH / 2 + 2, -deckD / 2 + 0.4);
    group.add(screenFrame);
    const screen = new THREE.Mesh(
      new THREE.PlaneGeometry(screenW, screenH),
      matScreen
    );
    screen.position.set(0, deckH + screenH / 2 + 2, -deckD / 2 + 0.65);
    screen.rotation.y = Math.PI;
    group.add(screen);

    // Accent strip
    const strip = new THREE.Mesh(
      new THREE.BoxGeometry(deckW, 0.18, 0.3),
      matAccent
    );
    strip.position.set(0, deckH, deckD / 2 + 0.1);
    group.add(strip);

    const audioAnchor = new THREE.Object3D();
    audioAnchor.position.set(0, 3.5, deckD / 2 + 4);
    group.add(audioAnchor);

    return {
      id: 'which-stage',
      label: 'Which Stage',
      group,
      position: new THREE.Vector3(),
      audioAnchor,
      audioFalloff: { ref: 50, max: 260 },
      screenQuad: makeScreenQuad(
        new THREE.Vector3(0, deckH + screenH / 2 + 2, -deckD / 2 + 0.65),
        screenW,
        screenH,
        Math.PI
      ),
      screenWidth: screenW,
      screenHeight: screenH
    };
  }
}
